import { useI18n } from "@/contexts/I18nContext";
import { ShieldCheck } from "lucide-react";

const CONTACT_EMAIL = "khalifa.belkhiter@lafarge.com";

const SECTIONS = [
  {
    title: "Responsable du traitement",
    content:
      "L'application Protocoles Sécurité (gestion des protocoles de sécurité HSE) est éditée et opérée par Lafarge pour ses besoins internes de transport et de logistique. Le responsable du traitement des données à caractère personnel est Lafarge, représenté pour toute question relative à cette politique par le contact indiqué en bas de page.",
  },
  {
    title: "Données collectées",
    content:
      "Lors de la connexion via votre compte Google professionnel, l'application reçoit votre nom, votre adresse e-mail et votre photo de profil. Les données que vous saisissez dans les protocoles (informations d'entreprise, responsables de site, produits transportés, etc.) sont également enregistrées. Aucun mot de passe n'est stocké par l'application : l'authentification est entièrement déléguée à Google.",
  },
  {
    title: "Où sont stockées les données ?",
    content:
      "Conformément à l'architecture de l'application, aucune base de données classique n'est utilisée. Toutes les données structurées (protocoles, référentiel HSE, utilisateurs, produits) sont stockées dans un classeur Google Sheets dédié, et tous les fichiers (PDF, plans de circulation, fiches de données de sécurité) sont stockés dans un espace Google Drive dédié, tous deux hébergés par Google et accessibles uniquement aux comptes autorisés par votre administrateur.",
  },
  {
    title: "Traitement par intelligence artificielle",
    content:
      "Certaines fonctionnalités s'appuient sur des modèles d'intelligence artificielle (Anthropic Claude et Google Gemini) pour : traduire automatiquement les protocoles du français vers l'anglais, extraire des informations depuis les fiches de données de sécurité (FDS/FHD), et générer les pictogrammes visuels du référentiel. Ces traitements portent sur le contenu des protocoles et des documents produits, et non sur des données personnelles sensibles. Le français reste toujours la version maîtresse et officielle de chaque protocole.",
  },
  {
    title: "Finalités du traitement",
    content:
      "Les données sont utilisées exclusivement pour la création, la validation, la traduction et l'archivage des protocoles de sécurité applicables aux opérations de chargement et de déchargement, ainsi que pour la gestion des accès et des rôles au sein de l'application.",
  },
  {
    title: "Durée de conservation",
    content:
      "Les protocoles et leurs versions sont conservés tant qu'ils sont nécessaires au suivi de la conformité HSE du site concerné, et archivés au-delà de leur date d'échéance conformément aux obligations réglementaires applicables au transport routier et fluvial.",
  },
  {
    title: "Vos droits",
    content:
      "Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification, d'effacement et d'opposition concernant vos données personnelles traitées par l'application. Pour exercer ces droits, contactez l'adresse indiquée ci-dessous.",
  },
  {
    title: "Sécurité",
    content:
      "L'accès à l'application est réservé aux comptes Google professionnels autorisés. Les rôles (Administrateur, Rédacteur…) déterminent les actions possibles pour chaque utilisateur. Les échanges avec Google Sheets, Google Drive et les services d'intelligence artificielle sont chiffrés en transit (HTTPS).",
  },
];

export default function Confidentialite() {
  const { t } = useI18n();
  return (
    <div className="p-4 md:p-6 max-w-3xl" data-testid="confidentialite-view">
      <div className="flex items-center gap-3 mb-2">
        <span className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5" />
        </span>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{t("nav.privacy")}</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">Politique de confidentialité de l'application Protocoles Sécurité.</p>

      <div className="space-y-5">
        {SECTIONS.map((s, idx) => (
          <div key={idx} data-testid={`privacy-section-${idx}`}>
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-1.5">{s.title}</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{s.content}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 border-t border-slate-200 dark:border-slate-800 pt-5">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Pour toute question relative à vos données personnelles ou à cette politique de confidentialité, contactez :
        </p>
        <a href={`mailto:${CONTACT_EMAIL}`} data-testid="privacy-contact-email" className="text-emerald-600 dark:text-emerald-400 font-medium hover:underline">
          {CONTACT_EMAIL}
        </a>
      </div>
    </div>
  );
}
